<template>
  <div class="clipping-tool">
    <div>
      <div class="text-left">{{ $t('Time frame') }}:
        <span class="duration">{{ clipDuration }} sec</span>
      </div>
      <div class="time-inputs">
        <input
          type="text"
          inputmode="numeric"
          class="form-control"
          v-model="startTimeFormatted"
          @input="updateInput('start')"
        />
        <span>-</span>
        <input
          type="text"
          inputmode="numeric"
          class="form-control"
          v-model="endTimeFormatted"
          @input="updateInput('end')"
        />
        <button class="button" @click="playPart">Preview</button>
      </div>
    </div>
    <div class="ruler-container" ref="container"
      @mousedown="startRulerDrag"
      @mousemove.capture="handleRulerDrag"
      @mouseup="stopRulerDrag"
      @mouseleave="stopRulerDrag"
      @touchstart="startRulerDrag"
      @touchmove.capture="handleRulerDrag"
      @touchend="stopRulerDrag"
      @touchcancel="stopRulerDrag"
      >
      <canvas ref="rulerCanvas" class="ruler"></canvas>
      <div
        class="clipping-rectangle"
        :style="rectangleStyle"
        @mousedown.stop="startRectangleDrag($event)"
        @touchstart.stop="startRectangleDrag($event)"
      >
        <div
          class="handle left-handle"
          @mousedown.stop="startHandleDrag('left', $event)"
          @touchstart.stop="startHandleDrag('left', $event)"
        ></div>
        <div class="clipped-duration">
          <span>{{ clipDuration }}</span>
        </div>
        <div
          class="handle right-handle"
          @mousedown.stop="startHandleDrag('right', $event)"
          @touchstart.stop="startHandleDrag('right', $event)"
        ></div>
      </div>
      <div class="playhead" :style="playheadStyle"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ClippingTimeRuller',
  props: {
    duration: {type: Number, required: true}, // Total duration in seconds
    paddingLeft: {type: Number, default: 40}, // Padding on the left side (in pixels)
    currentTime: {type: Number, default: 0}, // Current time to set the initial position
    playingTime: {type: Number, default: 0}, // Initial start time of the clipping rectangle
    minDuration: {type: Number, default: 0}, // Minimum clip duration in seconds
    maxDuration: {type: Number, default: 120}, // Maximum clip duration in seconds
    visibleDuration: {type: Number, default: 180}, // Visible duration in seconds
    defaultClipDuration: {type: Number, default: 30}, // Default clip duration in seconds
  },
  data() {
    return {
      ctx: null, // Canvas context
      offset: 0, // Horizontal scroll offset
      isRulerDragging: false, // Whether ruler dragging is active
      isRectangleDragging: false, // Whether rectangle dragging is active
      startX: 0, // Initial mouse position when dragging starts
      startOffset: 0, // Initial offset when dragging starts
      canvasWidth: 0, // Width of the canvas
      canvasHeight: 60, // Height of the canvas
      devicePixelRatio: window.devicePixelRatio || 1, // Device pixel ratio for crisp rendering
      startTime: this.currentTime, // Start time of the clipping rectangle
      endTime: this.currentTime + this.defaultClipDuration, // End time of the clipping rectangle
      dragHandle: null, // Which handle is being dragged ('left' or 'right')
      isHandleDragging: false, // Whether a handle is being dragged
      rectangleDragStartX: 0, // Initial mouse position when rectangle dragging starts
      rectangleDragStartTime: {start: 0, end: 0}, // Initial times when rectangle dragging starts
    };
  },
  computed: {
    startTimeFormatted: {
      get() {
        return this.formatTime(this.startTime);
      },
      set(value) {
        this.startTime = this.parseTime(value);
      },
    },
    endTimeFormatted: {
      get() {
        return this.formatTime(this.endTime);
      },
      set(value) {
        this.endTime = this.parseTime(value);
      },
    },
    pixelsPerSecond() {
      return +((this.canvasWidth - this.paddingLeft) / this.visibleDuration).toFixed(1);
    },
    rectangleStyle() {
      const left = this.paddingLeft +
        (this.startTime - this.offset / this.pixelsPerSecond) * this.pixelsPerSecond;
      const width = (this.endTime - this.startTime) * this.pixelsPerSecond;
      return {
        left: `${left}px`,
        width: `${width}px`,
      };
    },
    clipDuration() {
      // Calculate the clipped duration and ensure it stays within min/max bounds
      const duration = Math.round(this.endTime - this.startTime);
      return Math.max(this.minDuration, Math.min(duration, this.maxDuration));
    },
    playheadStyle() {
      const left = this.paddingLeft +
        (this.playingTime - this.offset / this.pixelsPerSecond) * this.pixelsPerSecond;
      return {
        left: `${left}px`,
      };
    },
  },
  watch: {
    currentTime(newTime) {
      const endTime = newTime + this.clipDuration;
      if (endTime > this.duration) {
        this.startTime = this.duration - this.clipDuration;
        this.endTime = this.duration;
      } else {
        this.startTime = newTime;
        this.endTime = endTime;
      }
      this.scrollToCurrentTime();
    },
    playingTime(newTime) {
      this.ensurePlayheadVisible(newTime);
    },
    duration() {
      this.clampDuration();
      this.drawRuler();
    },
  },
  methods: {
    initCanvas() {
      this.ctx = this.$refs.rulerCanvas.getContext('2d');
      const canvas = this.$refs.rulerCanvas;

      this.canvasWidth = this.$refs.container.clientWidth;
      canvas.width = this.canvasWidth * this.devicePixelRatio;
      canvas.height = this.canvasHeight * this.devicePixelRatio;
      canvas.style.width = `${this.canvasWidth}px`;
      canvas.style.height = `${this.canvasHeight}px`;

      this.ctx.scale(this.devicePixelRatio, this.devicePixelRatio);
      this.drawRuler();
      this.scrollToCurrentTime();
    },
    drawTick(time, isMajorTick) {
      const x = this.paddingLeft +
        (time - this.offset / this.pixelsPerSecond) * this.pixelsPerSecond;
      this.ctx.beginPath();
      this.ctx.moveTo(x, this.canvasHeight - 2);
      this.ctx.lineTo(x, this.canvasHeight -
        (isMajorTick ? this.canvasHeight - 5 : this.canvasHeight - 15));
      this.ctx.lineWidth = 1;
      this.ctx.strokeStyle = '#999';
      this.ctx.stroke();

      if (isMajorTick && time >= 0) {
        this.ctx.fillStyle = '#333333';
        this.ctx.font = '12px Open Sans';
        this.ctx.textAlign = 'left';
        this.ctx.fillText(this.formatTime(time), x + 4, 12);
      }
    },
    drawRuler() {
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Draw grey background rectangle with 20px indent from the top
      ctx.fillStyle = '#d9d9d9';
      ctx.fillRect(0, 20, this.canvasWidth, this.canvasHeight - 20);

      // Draw baseline
      ctx.beginPath();
      ctx.moveTo(0, this.canvasHeight - 1);
      ctx.lineTo(this.canvasWidth, this.canvasHeight - 1);
      ctx.strokeStyle = '#333333';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw ticks
      const startTime = this.offset / this.pixelsPerSecond;
      const endTime = startTime + this.visibleDuration;
      for (let time = Math.floor(startTime / 15) * 15; time <= endTime; time += 15) {
        this.drawTick(time, time % 30 === 0);
      }
    },
    modifyNum(num) {
      let result = num.toString().slice(1);
      const time = +result;
      if (time > 59) {
        result = result.slice(1);
      }
      return +result;
    },
    parseTime(timeString) {
      const parts = timeString.replace(/[^\d:]/g, '').split(':').map(Number);
      if (parts.length === 2) {
        if (parts[0] > 59 ) {
          parts[0] = this.modifyNum(parts[0]);
        }
        if (parts[1] > 59) {
          parts[1] = this.modifyNum(parts[1]);
        }
      }
      if (parts.length === 3) {
        if (parts[0] > 23) {
          parts[0] = this.modifyNum(parts[0]);
        }
        if (parts[1] > 59) {
          parts[1] = this.modifyNum(parts[1]);
        }
        if (parts[2] > 59) {
          parts[2] = this.modifyNum(parts[2]);
        }
      }
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      if (parts.length === 2) return parts[0] * 60 + parts[1];
      return 0;
    },
    formatTime(seconds) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      return hrs > 0 ?
        `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}` :
        `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    scrollToCurrentTime() {
      const targetOffset = this.startTime * this.pixelsPerSecond - this.paddingLeft;
      this.offset = Math.max(0,
          Math.min(targetOffset, this.duration * this.pixelsPerSecond - this.canvasWidth));
      this.drawRuler();
    },
    clampDuration() {
      // Ensure duration stays within min/max bounds
      const duration = this.endTime - this.startTime;
      if (duration < this.minDuration) {
        this.endTime = this.startTime + this.minDuration;
      } else if (duration > this.maxDuration) {
        this.endTime = this.startTime + this.maxDuration;
      }
      // Ensure endTime doesn't exceed total duration
      this.endTime = Math.min(this.endTime, this.duration);
      // Ensure startTime >= 0
      this.startTime = Math.max(this.startTime, 0);
    },
    updateInput(type) {
      const clipDuration = this.clipDuration;
      const startTime = this.parseTime(this.startTimeFormatted);
      const endTime = this.parseTime(this.endTimeFormatted);

      if (type === 'start') {
        this.startTime = startTime;
        if (startTime >= endTime) {
          this.endTime = startTime + clipDuration;
        }
      } else if (type === 'end') {
        this.endTime = endTime;
        if (endTime <= startTime) {
          this.startTime = endTime - clipDuration;
        } else if ((endTime - startTime) > this.maxDuration) {
          this.startTime = endTime - Math.max(this.minDuration, clipDuration);
        }
      }
      this.emitClippingTimeChange();
    },
    addDocumentListeners() {
      document.addEventListener('mousemove', this.handleRulerDrag);
      document.addEventListener('mouseup', this.stopRulerDrag);
    },
    removeDocumentListeners() {
      document.removeEventListener('mousemove', this.handleRulerDrag);
      document.removeEventListener('mouseup', this.stopRulerDrag);
    },
    getClientX(event) {
      return event.touches ? event.touches[0].clientX : event.clientX;
    },
    startRulerDrag(event) {
      if (this.isHandleDragging || this.isRectangleDragging) return;
      this.isRulerDragging = true;
      this.startX = this.getClientX(event);
      this.startOffset = this.offset;

      this.addDocumentListeners();
    },
    handleRulerDrag(event) {
      if (this.isRulerDragging) {
        const deltaX = this.getClientX(event) - this.startX;
        this.offset = Math.max(0, Math.min(
            this.startOffset - deltaX,
            this.duration * this.pixelsPerSecond - (this.canvasWidth - this.paddingLeft),
        ));
        requestAnimationFrame(() => this.drawRuler());
      }
    },
    stopRulerDrag() {
      this.isRulerDragging = false;
      this.removeDocumentListeners();
    },
    startRectangleDrag(event) {
      event.stopPropagation();
      this.isRectangleDragging = true;
      this.rectangleDragStartX = this.getClientX(event);
      this.rectangleDragStartTime = {start: this.startTime, end: this.endTime};

      this.$emit('start-drag');

      document.addEventListener('mousemove', this.handleRectangleDrag);
      document.addEventListener('mouseup', this.stopRectangleDrag);
      document.addEventListener('touchmove', this.handleRectangleDrag, {passive: false});
      document.addEventListener('touchend', this.stopRectangleDrag);
    },
    handleRectangleDrag(event) {
      if (this.isRectangleDragging) {
        const deltaX = this.getClientX(event) - this.rectangleDragStartX;
        const deltaTime = deltaX / this.pixelsPerSecond;
        this.startTime = Math.max(0, Math.min(
            this.rectangleDragStartTime.start + deltaTime,
            this.duration - (this.endTime - this.startTime),
        ));
        this.endTime = this.startTime +
          (this.rectangleDragStartTime.end - this.rectangleDragStartTime.start);
        this.clampDuration();

        // Auto-scroll when dragging the rectangle outside the visible area
        const rectLeft = this.paddingLeft +
          (this.startTime - this.offset / this.pixelsPerSecond) * this.pixelsPerSecond;
        const rectRight = rectLeft + (this.endTime - this.startTime) * this.pixelsPerSecond;

        if (rectLeft < this.paddingLeft) {
          // Scroll left
          this.offset = Math.max(0, this.offset -
            (this.paddingLeft - rectLeft) / this.pixelsPerSecond);
        } else if (rectRight > this.canvasWidth) {
          // Scroll right
          this.offset = Math.min(
              this.duration * this.pixelsPerSecond - this.canvasWidth,
              this.offset + (rectRight - this.canvasWidth) / this.pixelsPerSecond,
          );
        }
        this.drawRuler();
      }
    },
    stopRectangleDrag() {
      this.isRectangleDragging = false;

      this.emitClippingTimeChange();

      document.removeEventListener('mousemove', this.handleRectangleDrag);
      document.removeEventListener('mouseup', this.stopRectangleDrag);
      document.removeEventListener('touchmove', this.handleRectangleDrag);
      document.removeEventListener('touchend', this.stopRectangleDrag);
    },
    startHandleDrag(handle, event) {
      event.stopPropagation();
      this.isHandleDragging = true;
      this.dragHandle = handle;
      this.startX = this.getClientX(event);

      this.$emit('start-drag');

      document.addEventListener('mousemove', this.handleHandleDrag);
      document.addEventListener('mouseup', this.stopHandleDrag);
      document.addEventListener('touchmove', this.handleHandleDrag, {passive: false});
      document.addEventListener('touchend', this.stopHandleDrag);
    },
    handleHandleDrag(event) {
      if (!this.isHandleDragging) return;
      const deltaX = this.getClientX(event) - this.startX;
      const deltaTime = deltaX / this.pixelsPerSecond;

      if (this.dragHandle === 'left') {
        this.startTime = Math.max(
            0,
            Math.min(
                this.startTime + deltaTime,
                this.endTime - this.minDuration,
            ),
        );
        this.startTime = Math.max(this.startTime, this.endTime - this.maxDuration);
      } else if (this.dragHandle === 'right') {
        this.endTime = Math.min(
            this.duration,
            Math.max(
                this.endTime + deltaTime,
                this.startTime + this.minDuration,
            ),
        );
        this.endTime = Math.min(this.endTime, this.startTime + this.maxDuration);
      }
      this.startX = this.getClientX(event);
      this.clampDuration();
    },
    stopHandleDrag() {
      this.isHandleDragging = false;
      this.dragHandle = null;

      // Emit the event with the start position and duration
      this.emitClippingTimeChange();

      document.removeEventListener('mousemove', this.handleHandleDrag);
      document.removeEventListener('mouseup', this.stopHandleDrag);
      document.removeEventListener('touchmove', this.handleHandleDrag);
      document.removeEventListener('touchend', this.stopHandleDrag);
    },
    emitClippingTimeChange() {
      this.$emit('clip-change', {
        startPosition: Math.floor(this.startTime),
        duration: this.clipDuration,
      });
    },
    handleResize() {
      this.initCanvas();
    },
    playPart() {
      this.$emit('play-clip', {
        start: this.startTime,
        end: this.endTime,
      });
    },
    ensurePlayheadVisible(currentTime) {
      const playheadPosition = this.paddingLeft +
        (currentTime - this.offset / this.pixelsPerSecond) * this.pixelsPerSecond;
      const containerWidth = this.$refs.container.clientWidth;

      if (playheadPosition < this.paddingLeft) {
        // Scroll left
        // eslint-disable-next-line max-len
        this.offset = Math.max(0, this.offset - (this.paddingLeft - playheadPosition) / this.pixelsPerSecond);
      } else if (playheadPosition > containerWidth) {
        // Scroll right
        this.offset = Math.min(
            this.duration * this.pixelsPerSecond - containerWidth,
            this.offset + (playheadPosition - containerWidth) / this.pixelsPerSecond,
        );
      }
      this.drawRuler();
    },
  },
  mounted() {
    this.initCanvas();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
.clipping-tool {
  display: grid;
  gap: 0.5rem;
  position: relative;
  width: 100%;
}

.ruler-container {
  display: flex;
  position: relative;
  width: 100%;
  height: 60px;
  overflow: hidden;
  cursor: grab;
  user-select: none;
}

.ruler-container:active {
  cursor: grabbing;
}

.ruler {
  position: absolute;
  top: 0;
  left: 0;
}

.clipping-rectangle {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 20px;
  height: 2.5rem;
  background-color: var(--clipping-rectangle-background, #007bff);
  border-radius: 4px;
  overflow: hidden;
  cursor: move;
  touch-action: none;
}

.handle {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  width: 10px;
  height: 100%;
  background-color: var(--clipping-handle-background, #007bff);
  cursor: ew-resize;
  touch-action: none;
}

.handle::after {
  content: "";
  display: flex;
  width: 40%;
  height: 80%;
  background-color: #eee;
  border-radius: 2px;
}

.left-handle {
  left: 0;
}

.right-handle {
  right: 0;
}

.time-inputs {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.time-inputs input {
  width: 6rem;
}

.clipped-duration {
  color: var(--settings-body-background);
  font-size: 0.6rem;
}

.playhead {
  position: absolute;
  bottom: 0;
  width: 4px;
  height: 2.5rem;
  background-color: var(--clip-playhead-background, red);
  z-index: 10;
  pointer-events: none;
  border-radius: 3px;
  transform: translateX(-50%);
}

.button {
  --background: var(--theme-link-accent);
  --text: var(--settings-body-background);
  background-color: var(--background);
  color: var(--text);
  border: none;
  padding: 0.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 900;
  text-transform: uppercase;
}
</style>
